import React, { FunctionComponent, useState, ReactNode } from 'react'
import { unwrapResult } from '@reduxjs/toolkit'
import {
  Grid,
  Text,
  InputPhone,
  Button,
  InputOnChange,
  AllowCountriesConfig,
  usePhoneNumber,
  useThemePerClass,
  useIsDesignLanguage,
  DesignLanguage,
} from '@afterpaytouch/core'
import { ErrorCode } from '@afterpaytouch/portal-api/types'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'

import { Form, ProfileCard, AppLink } from '..'
import { Trans } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { verifyAccountMobile, useAppDispatch, updateConsumerMobile, useConsumerSelector } from '../../state'

import { Route } from '../../config/router'
import { CheckoutError, TerminalErrorCode } from '../../model'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { phoneNumberIsCleared } from '../../utils/phone-numbers'
import { useTranslation } from '../../utils/convergence'
import styles from './style.module.css'

interface Props {
  onSuccess: () => void
  onTerminalError: (e: CheckoutError) => void
}

const errorKeys: Partial<Record<ErrorCode, string>> = {
  [ErrorCode.ExistingPhoneNumber]: 'existingPhoneNumber',
  [ErrorCode.ExistingPhoneNumberSendEmail]: 'existingPhoneNumber',
}

export const AddMobile: FunctionComponent<Props> = ({ onSuccess, onTerminalError }) => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const themeClass = useThemePerClass(styles)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { textSize, buttonSize } = useCheckoutSizes()
  const { accountCountry, mobile: consumerMobile } = useConsumerSelector()
  const { parseMobile } = usePhoneNumber({ country: accountCountry })
  const isConsumerMobileDefined = consumerMobile && !phoneNumberIsCleared(consumerMobile)
  const consumerPhoneNumber = isConsumerMobileDefined ? parseMobile(consumerMobile) : ''
  const [mobile, setMobile] = useState(consumerPhoneNumber)
  const [mobileValid, setMobileValid] = useState(!!consumerPhoneNumber)
  const [isDisabled, setIsDisabled] = useState(false)
  const [error, setError] = useState('')
  const allowedCountry = {
    countryCode: accountCountry,
    name: t('common:countryName', { context: accountCountry }),
  }

  const onClick = async (ev): Promise<void> => {
    ev.preventDefault()
    setError('')
    setIsDisabled(true)

    try {
      if (mobileValid) {
        unwrapResult(await dispatch(verifyAccountMobile({ mobile })))
        unwrapResult(await dispatch(updateConsumerMobile(mobile)))
        onSuccess()
      } else {
        setError('addMobile:error.invalidMobile')
      }
    } catch (e) {
      const errorCode = isPortalApiError(e) ? e.errorCode : ErrorCode.Error
      if (errorCode === ErrorCode.SmsVerificationCodeGenerationNotAllowed) {
        onTerminalError({
          error: TerminalErrorCode.MaxAttempts,
          errorCode: ErrorCode.MaxAttemptsReached,
        })
      } else {
        setError(`addMobile:error.${errorKeys[errorCode] ?? 'unknown'}`)
      }
    } finally {
      setIsDisabled(false)
    }
  }

  const renderError = (errorMessage: string): ReactNode => (
    <Trans
      i18nKey={errorMessage}
      components={{
        login: <AppLink noMargin inline route={Route.ENTER_EMAIL} />,
      }}
    />
  )

  const onInputChange = (ev: InputOnChange, value: string, valid: boolean): void => {
    setMobile(value)
    setMobileValid(valid)
  }

  return (
    <Form>
      <div className={themeClass.profileWrapper}>
        <ProfileCard allowLogout />
      </div>
      <Grid>
        <Text size={textSize} id='add-mobile' color={isArcadeDesignLanguage ? 'Subtle' : undefined}>
          {t('addMobile:description')}
        </Text>
      </Grid>
      <Grid>
        <MaskedElement stylingModifier='wrap' actionName='Add Mobile Input'>
          <InputPhone
            label={t('addMobile:mobileInputLabel')}
            selectedCountry={accountCountry}
            allowedCountries={[allowedCountry as AllowCountriesConfig]} // TODO: Fix type hack: https://apglobal.atlassian.net/browse/CHECKOUT-1222
            value={consumerPhoneNumber}
            onChange={onInputChange}
            testNameSpace='add-mobile'
            error={Boolean(error)}
            errorText={renderError(error)}
          />
        </MaskedElement>
      </Grid>
      <Grid>
        <Grid.Item>
          <Button.Primary
            size={buttonSize}
            onClick={onClick}
            loading={isDisabled}
            disabled={isDisabled}
            type='submit'
            testNameSpace='add-mobile'
            padding='Fluid'
          >
            {t('common:continue')}
          </Button.Primary>
        </Grid.Item>
      </Grid>
    </Form>
  )
}
